import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getUrlParamsFetch, handleAxiosError, urlBackend } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import {
  Operadora, OperadoraPagingRes,
  AlertSeverityEnum,
  UseFetchEnabledParams,
  UseMutationsParams,
} from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';

const { get, post, put, remove } = erpApi();

///* tanStack query
export const useFetchOperadoras = ({
  enabled = true,
  params,
}: UseFetchEnabledParams<GetOperadorasParams> = {}) => {
  return useQuery({
    queryKey: ['operadoras-red', ...Object.values(params ?? {})],
    queryFn: () => getOperadoras(params),
    enabled: enabled,
  });
};

export const useGetOperadora = (id: string) => {
  return useQuery({
    queryKey: ['operadoras-red', id],
    queryFn: () => getOperadora(id),
  });
};

export const useCreateOperadora = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createOperadora,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['operadoras-red'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Operadora creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateOperadora = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateOperadora,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['operadoras-red'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Operadora actualizada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useDeleteOperadora = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteOperadora,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['operadoras-red'] });
    },
    onError: () => { },
  });
};

///* axios
export type GetOperadorasParams = Partial<Operadora> & {
  page?: number;
  page_size?: number;

  filterByState?: boolean;
};

export type CreateOperadoraParams = Omit<Operadora, 'id_operadora'>;
export type UpdateOperadoraParams = {
  id: string;
  data: Partial<CreateOperadoraParams>;
};

export const getOperadoras = async (params?: GetOperadorasParams) => {
  const stateParams = { ...params };

  // filterByState
  // if (stateParams.filterByState === false && stateParams.estado === undefined) {
  //   delete stateParams.estado;
  // } else if (stateParams.filterByState !== false) {
  //   stateParams.estado = true;
  // }
  // delete stateParams.filterByState;

  const queryParams = getUrlParamsFetch(stateParams);
  return get<OperadoraPagingRes>(`${urlBackend}api/v1/operadora/?${queryParams}`, true);
};

export const getOperadora = async (id: string) => {
  try {
    return await get<Operadora>(`${urlBackend}api/v1/operadora/${id}`, true);
  } catch (error) {
    handleAxiosError(error);
  }
};

export const createOperadora = async (data: CreateOperadoraParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<Operadora>(`${urlBackend}api/v1/operadora/`, data, true);
};

export const updateOperadora = async ({ id, data }: UpdateOperadoraParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<Operadora>(`${urlBackend}api/v1/operadora/${id}/`, data, true);
};

export const deleteOperadora = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;

  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;

  setIsGlobalLoading(true);
  const res = await remove<Operadora>(`${urlBackend}api/v1/operadora/${id}/`, true);

  setAlert(true, res.message, res.status);
  return res;
};
